import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
    Users,
    DollarSign,
    TrendingUp,
    TrendingDown,
    Target,
    AlertTriangle,
    Download,
    Printer,
    ChevronRight,
    ArrowUpRight,
    ArrowDownRight,
    Minus,
    FileText,
    BarChart3,
    PieChart as PieChartIcon,
    Calendar,
    X,
    Filter,
    Check
} from 'lucide-react';
import { useQuery } from 'react-query';
import api, { leadsAPI, organizationsAPI } from '../services/api';
import useClickOutside from '../hooks/useClickOutside';
import { formatCurrency } from '../utils/formatters';
import PieChart from '../components/charts/PieChart';
import BarChart from '../components/charts/BarChart';
import SalesPerformanceReport from '../components/SalesPerformanceReport';

// Pipeline stages in order
const PIPELINE_STAGES = [
    { id: 'new', label: 'New', color: '#3B82F6' },
    { id: 'contacted', label: 'Contacted', color: '#8B5CF6' },
    { id: 'proposal_sent', label: 'Proposal', color: '#F59E0B' },
    { id: 'quote_sent', label: 'Quote', color: '#EC4899' },
    { id: 'negotiation', label: 'Negotiation', color: '#14B8A6' },
    { id: 'won', label: 'Won', color: '#10B981' }
];

const STATUS_LABELS = {
    new: 'New',
    contacted: 'Contacted',
    proposal_sent: 'Proposal Sent',
    quote_sent: 'Quote Sent',
    negotiation: 'Negotiation',
    won: 'Won',
    lost: 'Lost'
};

const SOURCE_LABELS = {
    website: 'Website',
    phone: 'Phone',
    email: 'Email',
    social_media: 'Social Media',
    business_card: 'Business Card',
    referral: 'Referral',
    google_ads: 'Google Ads',
    other: 'Other'
};

const PRIORITY_LABELS = {
    high: 'High',
    medium: 'Medium',
    low: 'Low'
};

const FILTER_DIMENSIONS = {
    sources:    { label: 'Source',   options: SOURCE_LABELS },
    priorities: { label: 'Priority', options: PRIORITY_LABELS },
    statuses:   { label: 'Status',   options: STATUS_LABELS },
};

const EMPTY_FILTERS = { sources: [], priorities: [], statuses: [], assignedTo: [] };

const ReportsHub = () => {
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('month');
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [appliedDateRange, setAppliedDateRange] = useState(null);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [showFilters, setShowFilters] = useState(false);
    const filterRef = useClickOutside(showFilters, () => setShowFilters(false));
    const datePickerRef = useClickOutside(showDatePicker, () => setShowDatePicker(false));
    const [users, setUsers] = useState([]);
    const reportRef = useRef(null);

    // Org's pipeline stages drive the Sales Funnel labels. We fetch the
    // first pipeline (matches LeadsList / Pipeline board behaviour) and
    // hydrate the funnel against its stages. Falls back to the legacy
    // status enum below when no pipeline is configured.
    const { data: pipelineStages } = useQuery(
        ['reports-pipeline-stages'],
        async () => {
            const pipelines = await api.get('/pipelines').then((r) => r.data);
            if (!pipelines?.length) return [];
            return api.get(`/pipelines/${pipelines[0]._id}/stages`).then((r) => r.data);
        },
        { staleTime: 5 * 60 * 1000 }
    );

    useEffect(() => {
        fetchReportsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, appliedDateRange, filters]);

    // Owner picker source — fetched once. Includes inactive users so historical
    // ownership isn't hidden from the report.
    useEffect(() => {
        organizationsAPI.getUsers()
            .then((r) => setUsers(Array.isArray(r.data) ? r.data : (r.data?.users || [])))
            .catch(() => setUsers([]));
    }, []);

    const activeFilterCount = useMemo(
        () => Object.values(filters).reduce((acc, arr) => acc + arr.length, 0),
        [filters]
    );

    const fetchReportsData = async () => {
        try {
            setIsLoading(true);
            const params = { timeRange: period };
            if (period === 'custom' && appliedDateRange) {
                params.startDate = appliedDateRange.start;
                params.endDate = appliedDateRange.end;
            }
            // Each dimension flattens to a comma-joined string; backend accepts
            // both repeated and joined forms but joined keeps the URL short.
            for (const key of Object.keys(filters)) {
                if (filters[key]?.length) params[key] = filters[key].join(',');
            }
            const response = await leadsAPI.getStats(params);
            setStats(response.data);
            setError(null);
        } catch (err) {
            console.error('Reports Error:', err);
            setError(err);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleFilter = (dim, value) => {
        setFilters((prev) => {
            const cur = prev[dim] || [];
            return {
                ...prev,
                [dim]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value],
            };
        });
    };
    const clearAllFilters = () => setFilters(EMPTY_FILTERS);
    const removeFilter = (dim, value) => setFilters((p) => ({ ...p, [dim]: p[dim].filter((v) => v !== value) }));

    const renderFilterChipLabel = (dim, value) => {
        if (dim === 'assignedTo') {
            if (value === '__unassigned__') return 'Unassigned';
            const u = users.find((x) => String(x._id) === String(value));
            return u?.name || u?.email || 'User';
        }
        return FILTER_DIMENSIONS[dim]?.options?.[value] || value;
    };

    const handleApplyDateRange = () => {
        if (startDate && endDate) {
            setAppliedDateRange({ start: startDate, end: endDate });
            setPeriod('custom');
            setShowDatePicker(false);
        }
    };

    const handleClearCustomDate = () => {
        setAppliedDateRange(null);
        setStartDate('');
        setEndDate('');
        setPeriod('month');
    };

    const handlePeriodChange = (newPeriod) => {
        if (newPeriod !== 'custom') {
            setAppliedDateRange(null);
            setStartDate('');
            setEndDate('');
        }
        setPeriod(newPeriod);
        setShowDatePicker(false);
    };

    // Calculate all metrics from real data
    const metrics = useMemo(() => {
        if (!stats) return null;

        const overview = stats.overview || {};
        const statusBreakdown = stats.statusBreakdown || [];
        const stageBreakdown = stats.stageBreakdown || [];
        const sourceBreakdown = stats.sourceBreakdown || [];
        const priorityBreakdown = stats.priorityBreakdown || [];
        const previousPeriod = stats.previousPeriod;

        const totalLeads = overview.totalLeads || 0;
        const wonCount = overview.wonLeads || 0;
        const lostCount = overview.lostLeads || 0;
        const closedDeals = wonCount + lostCount;
        const winRate = closedDeals > 0 ? (wonCount / closedDeals) * 100 : 0;

        // Funnel — prefer the org's custom pipeline (stage labels the
        // operator typed in Pipeline Settings). Fall back to the legacy
        // hardcoded status enum if no pipeline is configured yet so
        // older accounts still see a meaningful funnel.
        const stageCounts = {};
        stageBreakdown.forEach((s) => { stageCounts[String(s._id)] = s.count; });
        const pipelineCounts = {};
        statusBreakdown.forEach((s) => { pipelineCounts[s._id] = s.count; });

        const useCustomPipeline = Array.isArray(pipelineStages) && pipelineStages.length > 0;
        const funnelStages = useCustomPipeline
            ? pipelineStages
                  // Won / lost stages live below the funnel as their own
                  // counters; including them here would shrink the chart
                  // (almost-zero closed counts dominate the visual).
                  .filter((s) => !s.isClosed)
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((s) => ({
                      id: String(s._id),
                      label: s.name,
                      color: s.color || '#6366F1',
                  }))
            : PIPELINE_STAGES;
        const sourceCounts = useCustomPipeline ? stageCounts : pipelineCounts;

        const funnelData = funnelStages.map((stage, index) => {
            const count = sourceCounts[stage.id] || 0;
            const prevStageCount = index > 0
                ? (sourceCounts[funnelStages[index - 1].id] || 0)
                : totalLeads;
            const conversionRate = prevStageCount > 0 ? (count / prevStageCount) * 100 : 0;

            return {
                ...stage,
                count,
                conversionRate: index === 0 ? 100 : conversionRate
            };
        });

        // Priority data
        const priorityData = priorityBreakdown.map(p => ({
            name: PRIORITY_LABELS[p._id] || p._id,
            value: p.count,
            priority: p._id
        }));

        // Source data
        const sourceData = sourceBreakdown.map(s => ({
            name: SOURCE_LABELS[s._id] || s._id,
            count: s.count
        }));

        // Status / stage data for the pie chart + Pipeline by Stage
        // table. Prefer the org's custom pipeline labels when available
        // (Pipeline Settings is the source of truth); fall back to the
        // legacy status enum so older tenants still see something.
        const statusData = useCustomPipeline
            ? pipelineStages
                  .slice()
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((s) => ({
                      _id: String(s._id),
                      name: s.name,
                      value: stageCounts[String(s._id)] || 0,
                  }))
                  .filter((row) => row.value > 0)
            : statusBreakdown
                  .filter((s) => s._id !== 'do_not_call')
                  .map((s) => ({
                      _id: s._id,
                      name: STATUS_LABELS[s._id] || s._id,
                      value: s.count,
                  }));

        // Period comparison
        let periodChange = null;
        if (previousPeriod) {
            const prevTotal = previousPeriod.totalLeads || 0;
            const prevWon = previousPeriod.wonLeads || 0;

            periodChange = {
                leads: prevTotal > 0 ? ((totalLeads - prevTotal) / prevTotal) * 100 : 0,
                won: prevWon > 0 ? ((wonCount - prevWon) / prevWon) * 100 : 0
            };
        }

        // Active pipeline (excluding won/lost)
        const activePipeline = totalLeads - wonCount - lostCount;

        return {
            totalLeads,
            wonCount,
            lostCount,
            winRate,
            activePipeline,
            totalValue: overview.totalValue || 0,
            currency: overview.currency || 'USD',
            funnelData,
            priorityData,
            sourceData,
            statusData,
            periodChange,
            statusBreakdown,
            highPriority: overview.highPriorityLeads || 0
        };
    }, [stats, pipelineStages]);

    // Helper to parse date string without timezone shift
    const parseDateString = (dateStr) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
    };

    // Period label for display
    const periodLabel = useMemo(() => {
        if (period === 'custom' && appliedDateRange) {
            const start = parseDateString(appliedDateRange.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            const end = parseDateString(appliedDateRange.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            return `${start} - ${end}`;
        }
        switch (period) {
            case 'week': return 'Last 7 Days';
            case 'month': return 'This Month';
            case 'quarter': return 'Last 3 Months';
            case 'year': return 'This Year';
            default: return 'All Time';
        }
    }, [period, appliedDateRange]);

    // Change indicator component
    const ChangeIndicator = ({ value, suffix = '%' }) => {
        if (value === null || value === undefined) return null;

        const isPositive = value > 0;
        const isNeutral = value === 0;

        return (
            <span className={`inline-flex items-center text-xs font-medium ${
                isNeutral ? 'text-gray-500' : isPositive ? 'text-green-600' : 'text-red-600'
            }`}>
                {isNeutral ? (
                    <Minus size={12} className="mr-0.5" />
                ) : isPositive ? (
                    <ArrowUpRight size={12} className="mr-0.5" />
                ) : (
                    <ArrowDownRight size={12} className="mr-0.5" />
                )}
                {Math.abs(value).toFixed(1)}{suffix}
            </span>
        );
    };

    // Export CSV
    const handleExport = () => {
        if (!stats || !metrics) return;

        let exportPeriodLabel;
        if (period === 'custom' && appliedDateRange) {
            const start = parseDateString(appliedDateRange.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            const end = parseDateString(appliedDateRange.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            exportPeriodLabel = `${start} - ${end}`;
        } else {
            switch (period) {
                case 'week': exportPeriodLabel = 'Last 7 Days'; break;
                case 'month': exportPeriodLabel = 'This Month'; break;
                case 'quarter': exportPeriodLabel = 'Last 3 Months'; break;
                case 'year': exportPeriodLabel = 'This Year'; break;
                default: exportPeriodLabel = 'All Time';
            }
        }
        const rows = [];

        rows.push(['Sales Performance Report']);
        rows.push([`Period: ${exportPeriodLabel}`]);
        rows.push([`Generated: ${new Date().toISOString()}`]);
        rows.push([]);

        rows.push(['KEY METRICS']);
        rows.push(['Metric', 'Value']);
        rows.push(['Total Leads', metrics.totalLeads]);
        rows.push(['Active Pipeline', metrics.activePipeline]);
        rows.push(['Deals Won', metrics.wonCount]);
        rows.push(['Deals Lost', metrics.lostCount]);
        rows.push(['Win Rate', `${metrics.winRate.toFixed(1)}%`]);
        rows.push(['Pipeline Value', formatCurrency(metrics.totalValue, metrics.currency)]);
        rows.push([]);

        rows.push(['PIPELINE BY STAGE']);
        rows.push(['Stage', 'Count', 'Conversion Rate']);
        metrics.funnelData.forEach(stage => {
            rows.push([stage.label, stage.count, `${stage.conversionRate.toFixed(1)}%`]);
        });
        rows.push([]);

        rows.push(['LEADS BY SOURCE']);
        rows.push(['Source', 'Count', 'Share']);
        metrics.sourceData.forEach(source => {
            const share = metrics.totalLeads > 0 ? (source.count / metrics.totalLeads) * 100 : 0;
            rows.push([source.name, source.count, `${share.toFixed(1)}%`]);
        });

        const csv = rows.map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sales-report-${period}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    // Print
    const handlePrint = () => {
        window.print();
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin mx-auto" />
                    <p className="mt-3 text-sm text-gray-500">Loading report data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <AlertTriangle className="h-10 w-10 text-red-500 mx-auto" />
                    <p className="mt-3 text-gray-900 font-medium">Failed to load report</p>
                    <button onClick={fetchReportsData} className="mt-3 text-sm text-primary-600 hover:underline">
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 print:bg-white">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 print:border-0">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Sales Performance Report</h1>
                            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                                {periodLabel} • Generated {new Date().toLocaleDateString()}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 print:hidden">
                            {/* Period Selector */}
                            <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                                {[
                                    { value: 'week', label: 'Week' },
                                    { value: 'month', label: 'Month' },
                                    { value: 'quarter', label: 'Quarter' },
                                    { value: 'year', label: 'Year' },
                                    { value: 'all', label: 'All' }
                                ].map(p => (
                                    <button
                                        key={p.value}
                                        onClick={() => handlePeriodChange(p.value)}
                                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                                            period === p.value && !appliedDateRange
                                                ? 'bg-gray-900 text-white'
                                                : 'bg-white text-gray-600 hover:bg-gray-50'
                                        }`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>

                            {/* Custom Date Button */}
                            <div className="relative" ref={datePickerRef}>
                                <button
                                    onClick={() => setShowDatePicker(!showDatePicker)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${
                                        period === 'custom'
                                            ? 'bg-primary-600 text-white border-primary-600'
                                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    <Calendar size={14} />
                                    {period === 'custom' && appliedDateRange
                                        ? `${parseDateString(appliedDateRange.start).toLocaleDateString('tr-TR')} - ${parseDateString(appliedDateRange.end).toLocaleDateString('tr-TR')}`
                                        : 'Custom'
                                    }
                                    {period === 'custom' && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleClearCustomDate(); }}
                                            className="ml-1 hover:bg-primary-700 rounded p-0.5"
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                </button>

                                {/* Date Picker Dropdown */}
                                {showDatePicker && (
                                    <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 min-w-[280px]">
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                                                <input
                                                    type="date"
                                                    value={startDate}
                                                    onChange={(e) => setStartDate(e.target.value)}
                                                    max={endDate || undefined}
                                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                                                <input
                                                    type="date"
                                                    value={endDate}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                    min={startDate || undefined}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div className="flex gap-2 pt-2">
                                                <button
                                                    onClick={() => setShowDatePicker(false)}
                                                    className="flex-1 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={handleApplyDateRange}
                                                    disabled={!startDate || !endDate}
                                                    className="flex-1 px-3 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Filter button — opens dimension picker popover. */}
                            <div className="relative" ref={filterRef}>
                                <button
                                    onClick={() => setShowFilters((v) => !v)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${
                                        activeFilterCount > 0
                                            ? 'bg-primary-50 text-primary-700 border-primary-200'
                                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    <Filter size={14} />
                                    Filter
                                    {activeFilterCount > 0 && (
                                        <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-primary-600 text-white rounded-full">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </button>

                                {showFilters && (
                                    <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 w-[320px] max-h-[70vh] overflow-y-auto">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="text-sm font-semibold text-gray-900">Filters</h4>
                                            {activeFilterCount > 0 && (
                                                <button onClick={clearAllFilters} className="text-xs text-gray-500 hover:text-gray-700">
                                                    Clear all
                                                </button>
                                            )}
                                        </div>

                                        {Object.entries(FILTER_DIMENSIONS).map(([dim, { label, options }]) => (
                                            <div key={dim} className="mb-4">
                                                <div className="text-xs font-medium text-gray-500 uppercase mb-2">{label}</div>
                                                <div className="space-y-1">
                                                    {Object.entries(options).map(([value, optLabel]) => {
                                                        const checked = filters[dim].includes(value);
                                                        return (
                                                            <button
                                                                key={value}
                                                                onClick={() => toggleFilter(dim, value)}
                                                                className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 text-left"
                                                            >
                                                                <span className={`w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 ${
                                                                    checked ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                                                                }`}>
                                                                    {checked && <Check size={12} className="text-white" />}
                                                                </span>
                                                                <span className="text-sm text-gray-700">{optLabel}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}

                                        <div className="mb-1">
                                            <div className="text-xs font-medium text-gray-500 uppercase mb-2">Owner</div>
                                            <div className="space-y-1">
                                                <button
                                                    onClick={() => toggleFilter('assignedTo', '__unassigned__')}
                                                    className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 text-left"
                                                >
                                                    <span className={`w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 ${
                                                        filters.assignedTo.includes('__unassigned__') ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                                                    }`}>
                                                        {filters.assignedTo.includes('__unassigned__') && <Check size={12} className="text-white" />}
                                                    </span>
                                                    <span className="text-sm text-gray-500 italic">Unassigned</span>
                                                </button>
                                                {users.map((u) => {
                                                    const id = String(u._id);
                                                    const checked = filters.assignedTo.includes(id);
                                                    return (
                                                        <button
                                                            key={id}
                                                            onClick={() => toggleFilter('assignedTo', id)}
                                                            className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 text-left"
                                                        >
                                                            <span className={`w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 ${
                                                                checked ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                                                            }`}>
                                                                {checked && <Check size={12} className="text-white" />}
                                                            </span>
                                                            <span className="text-sm text-gray-700 truncate">{u.name || u.email}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="w-px h-6 bg-gray-200" />

                            <button
                                onClick={handleExport}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                                title="Export CSV"
                            >
                                <Download size={18} />
                            </button>
                            <button
                                onClick={handlePrint}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                                title="Print"
                            >
                                <Printer size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Active filter chips strip — keeps applied filters visible
                without forcing the user to reopen the popover. */}
            {activeFilterCount > 0 && (
                <div className="bg-white border-b border-gray-200 print:hidden">
                    <div className="max-w-7xl mx-auto px-6 py-2 flex flex-wrap items-center gap-2">
                        <span className="text-xs text-gray-500">Filters:</span>
                        {Object.entries(filters).flatMap(([dim, values]) =>
                            values.map((v) => (
                                <button
                                    key={`${dim}:${v}`}
                                    onClick={() => removeFilter(dim, v)}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary-50 text-primary-700 border border-primary-200 rounded-full hover:bg-primary-100"
                                >
                                    <span className="font-medium">
                                        {dim === 'assignedTo' ? 'Owner' : FILTER_DIMENSIONS[dim].label}:
                                    </span>
                                    <span>{renderFilterChipLabel(dim, v)}</span>
                                    <X size={10} />
                                </button>
                            ))
                        )}
                        <button onClick={clearAllFilters} className="ml-auto text-xs text-gray-500 hover:text-gray-700 underline">
                            Clear all
                        </button>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6" ref={reportRef}>
                {/* Sales Performance (pipeline funnel, win rate, cycle length) */}
                <SalesPerformanceReport />

                {/* KPI Summary */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Leads</span>
                            {metrics?.periodChange && (
                                <ChangeIndicator value={metrics.periodChange.leads} />
                            )}
                        </div>
                        <div className="text-2xl font-semibold text-gray-900">{metrics?.totalLeads || 0}</div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pipeline Value</span>
                        </div>
                        <div className="text-2xl font-semibold text-gray-900">
                            {formatCurrency(metrics?.totalValue || 0, metrics?.currency)}
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Win Rate</span>
                        </div>
                        <div className="text-2xl font-semibold text-gray-900">
                            {metrics?.winRate.toFixed(1) || 0}%
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {metrics?.wonCount || 0} won / {metrics?.closedDeals || 0} closed
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Active Pipeline</span>
                        </div>
                        <div className="text-2xl font-semibold text-gray-900">{metrics?.activePipeline || 0}</div>
                        <div className="text-xs text-gray-500 mt-1">
                            {metrics?.highPriority || 0} high priority
                        </div>
                    </div>
                </div>

                {/* Conversion Funnel */}
                <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Sales Funnel</h2>

                    <div className="space-y-3">
                        {metrics?.funnelData.map((stage, index) => {
                            const maxCount = Math.max(...(metrics?.funnelData.map(s => s.count) || [1]), 1);
                            const widthPercent = (stage.count / maxCount) * 100;

                            return (
                                <div key={stage.id} className="flex items-center gap-4">
                                    <div className="w-24 flex-shrink-0">
                                        <div className="text-sm font-medium text-gray-700">{stage.label}</div>
                                        {index > 0 && (
                                            <div className="text-xs text-gray-400">
                                                {stage.conversionRate.toFixed(0)}% from prev
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="h-8 bg-gray-100 rounded overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: `${widthPercent}%` }}
                                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                                className="h-full rounded flex items-center justify-end px-2"
                                                style={{ backgroundColor: stage.color, minWidth: stage.count > 0 ? '2rem' : 0 }}
                                            >
                                                {stage.count > 0 && (
                                                    <span className="text-xs font-semibold text-white">{stage.count}</span>
                                                )}
                                            </motion.div>
                                        </div>
                                    </div>
                                    {index < (metrics?.funnelData.length || 0) - 1 && (
                                        <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Lost deals indicator */}
                    {(metrics?.lostCount || 0) > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-4">
                                <div className="w-24 flex-shrink-0">
                                    <div className="text-sm font-medium text-red-600">Lost</div>
                                </div>
                                <div className="flex-1">
                                    <div className="h-8 bg-gray-100 rounded overflow-hidden">
                                        <div
                                            className="h-full bg-red-500 rounded flex items-center justify-end px-2"
                                            style={{
                                                width: `${(metrics.lostCount / Math.max(...(metrics?.funnelData.map(s => s.count) || [1]), 1)) * 100}%`,
                                                minWidth: '2rem'
                                            }}
                                        >
                                            <span className="text-xs font-semibold text-white">{metrics.lostCount}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Lead Sources */}
                    <div className="bg-white rounded-lg border border-gray-200 p-5">
                        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Lead Sources</h2>
                        {(metrics?.sourceData?.length || 0) > 0 ? (
                            <BarChart
                                data={metrics.sourceData}
                                xKey="name"
                                yKey="count"
                                height={250}
                                color="#6366F1"
                                showGrid={true}
                                showTooltip={true}
                            />
                        ) : (
                            <div className="h-[250px] flex items-center justify-center text-gray-400 text-sm">
                                No source data available
                            </div>
                        )}
                    </div>

                    {/* Status Distribution */}
                    <div className="bg-white rounded-lg border border-gray-200 p-5">
                        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Status Distribution</h2>
                        {(metrics?.statusData?.length || 0) > 0 ? (
                            <PieChart
                                data={metrics.statusData}
                                dataKey="value"
                                nameKey="name"
                                height={250}
                                showLegend={true}
                                showTooltip={true}
                            />
                        ) : (
                            <div className="h-[250px] flex items-center justify-center text-gray-400 text-sm">
                                No status data available
                            </div>
                        )}
                    </div>
                </div>

                {/* Data Tables */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Pipeline by Stage */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="px-5 py-3 border-b border-gray-200">
                            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Pipeline by Stage</h2>
                        </div>
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                                    <th className="px-5 py-2 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                                    <th className="px-5 py-2 text-right text-xs font-medium text-gray-500 uppercase">%</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {metrics?.statusData?.map((row) => {
                                    const pct = metrics.totalLeads > 0
                                        ? (row.value / metrics.totalLeads) * 100
                                        : 0;
                                    return (
                                        <tr key={row._id} className="hover:bg-gray-50">
                                            <td className="px-5 py-2.5 text-sm text-gray-900">{row.name}</td>
                                            <td className="px-5 py-2.5 text-sm text-gray-600 text-right tabular-nums">
                                                {row.value}
                                            </td>
                                            <td className="px-5 py-2.5 text-sm text-gray-600 text-right tabular-nums">
                                                {pct.toFixed(1)}%
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Priority Breakdown */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="px-5 py-3 border-b border-gray-200">
                            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Priority Breakdown</h2>
                        </div>
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                                    <th className="px-5 py-2 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                                    <th className="px-5 py-2 text-right text-xs font-medium text-gray-500 uppercase">%</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {['high', 'medium', 'low'].map(priority => {
                                    const data = metrics?.priorityData?.find(p => p.priority === priority);
                                    const count = data?.value || 0;
                                    const pct = metrics?.totalLeads > 0
                                        ? (count / metrics.totalLeads) * 100
                                        : 0;

                                    return (
                                        <tr key={priority} className="hover:bg-gray-50">
                                            <td className="px-5 py-2.5 text-sm text-gray-900 flex items-center gap-2">
                                                <span className={`w-2 h-2 rounded-full ${
                                                    priority === 'high' ? 'bg-red-500' :
                                                    priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                                                }`} />
                                                {PRIORITY_LABELS[priority]}
                                            </td>
                                            <td className="px-5 py-2.5 text-sm text-gray-600 text-right tabular-nums">
                                                {count}
                                            </td>
                                            <td className="px-5 py-2.5 text-sm text-gray-600 text-right tabular-nums">
                                                {pct.toFixed(1)}%
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-8 pt-4 border-t border-gray-200 text-center print:mt-12">
                    <p className="text-xs text-gray-400">
                        Report generated on {new Date().toLocaleString()} • Data reflects {periodLabel.toLowerCase()} metrics
                    </p>
                </div>
            </div>

            {/* Print Styles */}
            <style>{`
                @media print {
                    @page {
                        size: A4 portrait;
                        margin: 14mm 12mm;
                    }
                    html, body {
                        background: #ffffff !important;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                        color-adjust: exact;
                    }
                    body {
                        font-size: 10pt;
                        line-height: 1.35;
                    }
                    .print\\:hidden { display: none !important; }
                    .print\\:bg-white { background-color: white !important; }
                    .print\\:border-0 { border: none !important; }

                    /* Kill gradients / shadows that print poorly */
                    .bg-gradient-to-br, .bg-gradient-to-r, .bg-gradient-to-b {
                        background-image: none !important;
                        background-color: #ffffff !important;
                    }
                    .shadow, .shadow-sm, .shadow-md, .shadow-lg, .shadow-xl {
                        box-shadow: none !important;
                    }

                    /* Every card / section stays on one page when possible */
                    .card, .bg-white, [class*="rounded-xl"], [class*="rounded-lg"] {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                    h1, h2, h3, h4 {
                        break-after: avoid;
                        page-break-after: avoid;
                    }

                    /* Page sectioning: funnel, summary, breakdowns each on own page if they don't fit */
                    .report-section {
                        break-inside: avoid;
                        page-break-inside: avoid;
                        margin-bottom: 10mm !important;
                    }

                    /* Remove viewport-height constraints that truncate content */
                    [class*="max-h-"], [class*="min-h-"], [class*="h-["] {
                        max-height: none !important;
                        min-height: 0 !important;
                        height: auto !important;
                    }

                    /* Hide charts' interactive legends / tooltips if any */
                    .recharts-tooltip-wrapper, .recharts-legend-wrapper {
                        display: none !important;
                    }

                    /* Container: let it flow the full page width */
                    .max-w-7xl {
                        max-width: 100% !important;
                        padding: 0 !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default ReportsHub;
